// Pre-order line-item properties on every add-to-cart form (app contract):
// an armed variant must post _preorder/_preorder_batch/_preorder_eta so
// Klaviyo's Placed Order event can key on them — a missed form means a
// pre-order confirmed by a "ships in 1–3 days" email.
//
// Liquid renders the initial inputs plus a per-variant map
// (script[data-lettoria-preorder], armed variants only) INSIDE each form.
// The native variant flow only rewrites the hidden id input
// (product-form.js #onProductSelect) — the property inputs would go stale on
// card-swatch and quick-add-modal switches — so this module re-asserts the
// three inputs from that map:
//  - after every productSelect settles (same setTimeout(0) re-assert timing
//    as lettoria-pdp.js, which owns the PDP section; both write identical
//    values from the same map, so overlap is idempotent)
//  - at submit-button click, document CAPTURE phase: the click always
//    precedes the framework's document-capture submit serialization (and
//    implicit Enter submission fires click on the default button), so the
//    posted pair (id + properties) can never disagree.
// Forms without a map (no armed variant) are left untouched.
import { StandardEvents } from '@shopify/events';
import { preorderPropsForVariant, applyPreorderInputs } from '@theme/lettoria-preorder-property-builder';

function syncForm(form) {
  // Forms without a map have no armed variant — leave their (absent) inputs
  // untouched rather than trusting a document-wide lookup.
  if (!form.querySelector('script[data-lettoria-preorder]')) return;
  const id = form.querySelector('input[name="id"]')?.value;
  applyPreorderInputs(form, preorderPropsForVariant(id, form));
}

function syncScope(scope) {
  for (const form of scope.querySelectorAll('form[data-type="add-to-cart-form"]')) syncForm(form);
}

document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target instanceof Element ? event.target : null;
  // Same scoping ladder as ProductFormComponent's own listener — the card's
  // quick-add form, the quick-add dialog's cloned form, or the section's
  // main form, whichever the select happened inside.
  const scope = target?.closest('product-card, dialog, .shopify-section') ?? document;
  // The native handler (registered on a descendant, so it chains onto the
  // promise first) rewrites the id input; the deferred re-assert then reads
  // that settled id, keeping id + properties consistent even when rapid
  // switches resolve out of order.
  event.promise?.then(() => setTimeout(() => syncScope(scope), 0)).catch(() => {});
});

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('button[type="submit"][name="add"]');
    const form = button?.closest('form[data-type="add-to-cart-form"]');
    if (form) syncForm(form);
  },
  { capture: true }
);
